import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Chip,
    Stack,
    Typography,
    useMediaQuery,
    IconButton,
    useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { getMinDisplayPrice, kmBetween } from '../../../../shared/utils';
import { CustomSmallListChip } from '../../../../layout/styledcomponents/CustomChip';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';

type Owner = { id: number; name: string };
type Photo = { id: string; src: string };
type Experience = {
    id: number;
    type: string;
    name: string;
    lat: number;
    lng: number;
    state?: string;
    suburb?: string;
    isAvailable?: boolean;
    rating?: number;
    photos?: Photo[];
    charges?: any[];
};

type Props = {
    current: Experience;
    all: Experience[];
    radiusKm?: number;
    maxResults?: number;
};

export default function SimilarExperiences({
    current,
    all,
    radiusKm = 25,
    maxResults = 12,
}: Props) {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const urlRef = window.location.href.split('/')[3];

    const railRef = useRef<HTMLDivElement | null>(null);
    const [canSlide, setCanSlide] = useState(false);
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(false);

    const items = useMemo(() => {
        if (!current?.lat || !current?.lng) return [];
        const here = { lat: current.lat, lng: current.lng };

        const filtered = (all || [])
            .filter((x) => x.id !== current.id)
            .filter((x) => !!x.lat && !!x.lng)
            .map((x) => {
                const dist = kmBetween(here, { lat: x.lat, lng: x.lng });
                return { ...x, _distanceKm: dist };
            })
            .filter((x) => x._distanceKm <= radiusKm)
            .sort((a, b) => a._distanceKm - b._distanceKm);

        filtered.sort((a: any, b: any) => {
            const av = (b.isAvailable ? 1 : 0) - (a.isAvailable ? 1 : 0);
            if (av !== 0) return av;
            const pa =
                getMinDisplayPrice(a.charges) ?? Number.POSITIVE_INFINITY;
            const pb =
                getMinDisplayPrice(b.charges) ?? Number.POSITIVE_INFINITY;
            return pa - pb;
        });

        return filtered.slice(0, maxResults);
    }, [all, current, radiusKm, maxResults]);

    useEffect(() => {
        const el = railRef.current;
        if (!el) return;

        const update = () => {
            const max = el.scrollWidth - el.clientWidth;
            setCanSlide(max > 8);
            const sl = Math.round(el.scrollLeft);
            setAtStart(sl <= 0);
            setAtEnd(sl >= max - 1);
        };

        let raf = 0;
        const onScroll = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(update);
        };
        const onResize = () => update();

        update();
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        return () => {
            cancelAnimationFrame(raf);
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
        };
    }, [items.length]);

    if (!items.length) return null;

    const getStride = () => {
        const el = railRef.current;
        if (!el) return 320;
        const first = el.children[0] as HTMLElement | undefined;
        const second = el.children[1] as HTMLElement | undefined;

        if (first && second) {
            const a = first.getBoundingClientRect();
            const b = second.getBoundingClientRect();
            const cardStride = b.left - a.left;
            if (cardStride > 0) {
                const perPage = Math.max(
                    1,
                    Math.floor(el.clientWidth / cardStride) - 1,
                );
                return perPage * cardStride;
            }
        }

        let gapPx = 16;
        try {
            const gap =
                getComputedStyle(el).columnGap || getComputedStyle(el).gap;
            if (gap) gapPx = parseInt(gap, 10) || gapPx;
        } catch {}
        const width = first ? first.getBoundingClientRect().width : 304;
        return width + gapPx;
    };

    const nudge = (dir: -1 | 1) => {
        const el = railRef.current;
        if (!el) return;
        const stride = getStride();
        el.scrollBy({ left: dir * stride, behavior: 'smooth' });
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Stack
                direction='row'
                alignItems='baseline'
                justifyContent='space-between'
                sx={{ px: 1, mb: 1 }}
            >
                {urlRef !== 'reservationRequest' && (
                    <Typography variant='h2' sx={{ mb: 2 }}>
                        More in the area{' '}
                    </Typography>
                )}

                <Chip
                    size='small'
                    label={`${Math.min(items.length, maxResults)} nearby`}
                    sx={{
                        bgcolor: theme.palette.textAccent.contrastText,
                        color: theme.palette.textAccent.main,
                        borderColor: theme.palette.textAccent.main,
                    }}
                    variant='outlined'
                />
            </Stack>

            <Box sx={{ position: 'relative' }}>
                {!isMobile && canSlide && (
                    <IconButton
                        aria-label='Scroll left'
                        onClick={() => nudge(-1)}
                        disabled={atStart}
                        sx={{
                            position: 'absolute',
                            top: '49%',
                            left: 8,
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                            border: '1px solid',
                            borderColor: 'grey.200',
                            opacity: atStart ? 0.4 : 0.95,
                            '&:hover': {
                                bgcolor: 'background.paper',
                                opacity: 1,
                            },
                            width: 36,
                            height: 36,
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                )}

                <Box
                    ref={railRef}
                    sx={
                        isMobile
                            ? {
                                  display: 'grid',
                                  gridTemplateColumns: '1fr',
                                  gap: 2,
                                  px: 1,
                                  pb: 2,
                              }
                            : {
                                  display: 'flex',
                                  gap: 2,
                                  overflowX: 'auto',
                                  pb: 1,
                                  px: 1,
                                  scrollSnapType: 'x mandatory',
                                  '& > *': {
                                      scrollSnapAlign: 'start',
                                      flex: '0 0 auto',
                                      minWidth: 280,
                                      maxWidth: 320,
                                  },

                                  WebkitMaskImage:
                                      'linear-gradient(90deg, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)',
                                  maskImage:
                                      'linear-gradient(90deg, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)',
                                  '&::-webkit-scrollbar': { display: 'none' },
                                  msOverflowStyle: 'none',
                                  scrollbarWidth: 'none',
                              }
                    }
                >
                    {items.map((exp: any) => {
                        const price = getMinDisplayPrice(exp.charges);
                        const photo = exp.photos?.[0]?.src;
                        const distanceLabel =
                            exp._distanceKm < 1
                                ? `${Math.round(
                                      exp._distanceKm * 1000,
                                  )} metres away`
                                : `${exp._distanceKm.toFixed(1)} km away`;

                        return (
                            <Card
                                key={exp.id}
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                }}
                            >
                                <CardActionArea
                                    onClick={() =>
                                        navigate(
                                            `/viewExperiences/${exp.type}/${exp.id}`,
                                        )
                                    }
                                >
                                    <CardMedia
                                        component='img'
                                        height={180}
                                        image={
                                            photo ||
                                            'https://placehold.co/640x360?text=Experience'
                                        }
                                        alt={exp.name}
                                    />
                                    <CardContent sx={{ p: 2 }}>
                                        {' '}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '2px',
                                                zIndex: 10,
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <CustomSmallListChip
                                                variant='filled'
                                                size='small'
                                                label={
                                                    exp.type
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                    exp.type.slice(1)
                                                }
                                                style={{
                                                    fontWeight: 'bold',
                                                    color: 'primary.main',
                                                    marginTop: '-5px',
                                                    padding: '4px 4px',
                                                    fontSize: '0.60rem',
                                                    letterSpacing: '0.5px',
                                                    backgroundColor:
                                                        'rgba(255, 255, 255, 0.8)',
                                                    boxShadow:
                                                        '0 2px 4px rgba(0,0,0,0.2)',
                                                    borderRadius: '10px',
                                                }}
                                            />
                                        </Box>{' '}
                                        <Typography
                                            variant='subtitle1'
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                lineHeight: 1.2,
                                            }}
                                            noWrap
                                        >
                                            {exp.name}
                                        </Typography>{' '}
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            justifyContent='space-between'
                                            // sx={{ mb: 0.5 }}
                                        >
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    fontSize: '12px',
                                                    mt: 0.5,
                                                }}
                                                noWrap
                                            >
                                                {exp.suburb
                                                    ? `${exp.suburb}, ${
                                                          exp.state || ''
                                                      }`
                                                    : exp.state || ''}
                                            </Typography>
                                            <Typography
                                                variant='caption'
                                                sx={{ fontSize: '12px' }}
                                            >
                                                {distanceLabel}
                                            </Typography>
                                        </Stack>
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            spacing={1}
                                            sx={{ mt: 1 }}
                                        >
                                            {typeof price === 'number' && (
                                                <Typography
                                                    variant='caption'
                                                    sx={{
                                                        ml: 'auto',
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    From $
                                                    {price.toLocaleString()}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <CustomButton
                                            fullWidth
                                            variant='outlined'
                                            color='primary'
                                            sx={{
                                                mt: 1.5,
                                            }}
                                        >
                                            View & book
                                        </CustomButton>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </Box>

                {/* Right arrow (desktop only) */}
                {!isMobile && canSlide && (
                    <IconButton
                        aria-label='Scroll right'
                        onClick={() => nudge(1)}
                        disabled={atEnd}
                        sx={{
                            position: 'absolute',
                            top: '49%',
                            right: 8,
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                            border: '1px solid',
                            borderColor: 'grey.200',
                            opacity: atEnd ? 0.4 : 0.95,
                            '&:hover': {
                                bgcolor: 'background.paper',
                                opacity: 1,
                            },
                            width: 36,
                            height: 36,
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
}
